import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { Calendar, Bell, Filter, Clock, Database, Zap, AlertTriangle, ArrowRight } from 'lucide-react';
import { siteConfig } from '@/config/site';

const features = [
    {
        icon: Zap,
        title: 'Real-Time Updates',
        description: 'Stay informed with live economic event data as it happens across global markets.',
    },
    {
        icon: Filter,
        title: 'Impact Filtering',
        description: 'Filter events by impact level — high, medium, or low — to focus on what matters most.',
    },
    {
        icon: Bell,
        title: 'Custom Alerts',
        description: 'Set personalized alerts for upcoming events so you never miss a market-moving release.',
    },
    {
        icon: Database,
        title: 'Historical Data',
        description: 'Access past economic releases and compare actual vs. forecast values over time.',
    },
];

export default function EconomicCalendar() {
    return (
        <AppLayout title={`Economic Calendar - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <Badge className="mb-6">Trading Tools</Badge>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Economic <span className="text-gradient">Calendar</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                            Track high-impact economic events from around the world. Plan your trades
                            around key data releases and central bank announcements.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Placeholder Content */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-12 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 border border-primary/10 mb-6">
                                <Calendar className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-4">
                                Economic Calendar Coming Soon
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                                Our economic calendar will display upcoming and past economic events,
                                including GDP releases, employment data, interest rate decisions, and more.
                                Each event shows the expected, previous, and actual values along with its
                                potential market impact.
                            </p>
                            <div className="inline-flex items-center gap-2 rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
                                <AlertTriangle className="w-4 h-4 text-primary" />
                                <span>Integration note: Connect to real-time economic data feeds</span>
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Features */}
            <Section className="py-20 bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Powerful"
                        highlight="Features"
                        description="Everything you need to stay ahead of market-moving economic events."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature) => (
                        <motion.div key={feature.title} variants={staggerItem}>
                            <Card className="text-center p-6 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors h-full">
                                <CardContent className="p-0">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Ready to Trade the <span className="text-gradient">News</span>?
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Get started with {siteConfig.brand.name} and stay ahead of every major economic event.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </a>
                            <Link to="/tools/economic-calendar">
                                <Button variant="outline" size="lg">Explore Tools</Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
